/**
 * Criar ou renomear uma empresa (raiz da KB, spec 2026-10-21-kb-raizes, R1 e R8). Só
 * para quem vê tudo; o backend confere de novo. A trilha é `kb.raiz.create` e
 * `kb.raiz.update`. A empresa nova entra depois das que existem.
 */
import { useEffect, useState } from 'react'
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createKbRaiz, renameKbRaiz } from '@/api/kb'
import { mensagemDeErro } from './acesso'

export const NOME_DA_RAIZ_MAX = 80

export type AlvoDaRaiz =
  | { modo: 'criar'; ordem: number }
  | { modo: 'renomear'; raizId: number; nomeAtual: string }

interface Props {
  alvo: AlvoDaRaiz | null
  onClose: () => void
  onCriada?: (raizId: number) => void
}

export function DialogoDeRaiz({ alvo, onClose, onCriada }: Props) {
  const qc = useQueryClient()
  const [nome, setNome] = useState('')

  useEffect(() => {
    if (alvo) setNome(alvo.modo === 'renomear' ? alvo.nomeAtual : '')
  }, [alvo])

  const salvar = useMutation({
    mutationFn: () => {
      const limpo = nome.trim()
      return alvo!.modo === 'criar' ? createKbRaiz(limpo, alvo!.ordem) : renameKbRaiz(alvo!.raizId, limpo)
    },
    onSuccess: (raiz) => {
      qc.invalidateQueries({ queryKey: ['kb-raizes'] })
      notifications.show({ color: 'teal', title: alvo!.modo === 'criar' ? 'Empresa criada' : 'Nome atualizado', message: raiz.nome })
      if (alvo!.modo === 'criar') onCriada?.(raiz.id)
      onClose()
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Não foi possível salvar', message: mensagemDeErro(e, 'Falha ao salvar a empresa.') }),
  })

  const limpo = nome.trim()
  const longo = limpo.length > NOME_DA_RAIZ_MAX
  const igual = alvo?.modo === 'renomear' && limpo === alvo.nomeAtual

  return (
    <Modal opened={alvo !== null} onClose={onClose}
      title={<Text fw={700}>{alvo?.modo === 'criar' ? 'Nova empresa' : 'Renomear empresa'}</Text>}>
      <Stack gap="sm">
        <TextInput
          label="Nome"
          value={nome}
          onChange={(e) => setNome(e.currentTarget.value)}
          error={longo ? `Até ${NOME_DA_RAIZ_MAX} caracteres.` : undefined}
          data-autofocus
        />
        <Text size="xs" c="dimmed">
          A empresa agrupa organizações na árvore e não guarda documento. Mudar o nome ou o lugar de uma organização não muda quem lê as pastas dela.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button loading={salvar.isPending} disabled={limpo === '' || longo || igual} onClick={() => salvar.mutate()}>Salvar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
